import { useEffect } from 'react'
import { NavigationType, Outlet, useLocation, useNavigationType } from 'react-router-dom'

/**
 * Reset the window scroll position when the router pushes a new route.
 *
 * A single-page app keeps the document scroll offset across a client-side
 * navigation, so following a footer link from the bottom of the landing page
 * opened /press, /subprocessors or a comparison page already scrolled to the
 * bottom — the reader landed in the middle of the legal small print with no
 * indication there was a page above them.
 *
 * Three cases, deliberately distinguished:
 *
 * - POP (back / forward / swipe-back) — do nothing. The browser restores the
 *   offset the entry was left at, which is the whole point of going back.
 * - A location with a hash — scroll that element into view instead of the top,
 *   so `/guide/x#section` still lands on the section.
 * - Everything else (PUSH / REPLACE) — top.
 *
 * The in-app routes scroll a `<main>` element rather than the document, and
 * AppLayout owns that (it has the ref); this component covers the public and
 * marketing routes, which scroll the document, and is a harmless no-op inside
 * the app shell where the window offset is already 0.
 */
export function ScrollToTopOnNavigate() {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    if (navigationType === NavigationType.Pop) return

    if (hash) {
      // The target may belong to a lazy chunk that has not painted yet; falling
      // through to the top is better than staying wherever the previous page was.
      const target = document.getElementById(decodeURIComponent(hash.slice(1)))
      if (target) {
        target.scrollIntoView()
        return
      }
    }

    window.scrollTo(0, 0)
  }, [pathname, hash, navigationType])

  return null
}

/** Pathless layout route: installs the scroll reset above every route in the tree. */
export function RootScrollLayout() {
  return (
    <>
      <ScrollToTopOnNavigate />
      <Outlet />
    </>
  )
}
